import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

export interface UVChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
}

export const uvChart = (options: UVChartOptions): ChartConfig => {
  const {data, theme, timezone, units, granularity, height = 250, comparisonData, comparisonLabel, dateTimeConfig} = options;

  const hasRange = ['H', '3H', '6H', 'D', 'W', 'M'].includes(granularity);

  const series: SeriesConfig[] = [
    {
      id: 'uv',
      type: 'line',
      dataKey: 'UV',
      name: 'UV Index',
      color: theme.colors.warning,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: 2,
    },
  ];

  if (hasRange) {
    series.push({
      id: 'uv-range',
      type: 'areaRange',
      dataKey: 'UV_RANGE',
      rangeKey: 'UV_RANGE',
      name: 'Min / Max',
      color: theme.colors.warning,
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.2,
      lineWidth: 0,
      showMarkers: false,
      zIndex: 0,
    });
  }

  if (comparisonData) {
    series.push({
      id: 'uv-comparison',
      type: 'line',
      dataKey: 'UV_COMP',
      name: comparisonLabel ?? 'Comparison',
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      dashArray: '5 3',
      showMarkers: false,
      zIndex: 1,
    });
  }

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({type: 'datetime', timezone, granularity, theme}),
    yAxes: [
      createYAxisConfig({
        id: 'uv',
        metric: 'UV',
        units,
        theme,
        domain: [0, 'auto'],
        unitLabel: 'UV',
        precision: 1,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'UV',
      units,
      theme,
      dateTimeConfig,
    }),
    legend: createLegendConfig(series.length > 2, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
